package com.evoapps.evoshift.data.session

import kotlin.coroutines.cancellation.CancellationException

/**
 * What a signed-in session leaves on the device and a sign-out removes (SH-177): the Firestore
 * offline cache with the pay rates, contact details and financials the person could read. On a
 * shared tablet or laptop the next person must not be served them from the cache.
 */
interface LocalCache {
    /** Gives writes still on the device a moment to reach the server (before the auth sign-out). */
    suspend fun flush()

    /** Removes the cache. */
    suspend fun clear()
}

/**
 * Ending the session on this device: the cache is flushed, the auth sign-out runs (the screens
 * leave and their listeners stop), then the cache is cleared. Clearing never fails a sign-out: a
 * failed step is logged and the person is signed out either way.
 */
class SessionEnd(private val signOutAuth: suspend () -> Unit, private val cache: LocalCache) {

    suspend fun signOut() {
        quietly("flush") { cache.flush() }
        signOutAuth()
        clearCache()
    }

    /** After the server deleted the account (the auth sign-out already happened). */
    suspend fun clearCache() = quietly("clear") { cache.clear() }

    private suspend fun quietly(step: String, block: suspend () -> Unit) {
        try {
            block()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("Local cache $step failed: ${e.message}")
        }
    }
}
